import { useMemo } from "react";
import Chart from "react-apexcharts";

import { spansMultipleYears } from "~/helpers/formatDate";
import { formatMetricOrDimensionName } from "~/pages/GoogleDashboardPage/utils/formatMetricOrDimensionName";
import { useChartTheme } from "~/pages/GoogleDashboardPage/utils/useChartTheme";

import type {
  GoogleAnalyticsPropertyRealtimeReport,
  GoogleAnalyticsPropertyReport,
} from "../../../../types/RenderableWidget.type";
import {
  formatDimensionValue,
  parseDimensionDate,
} from "../../utils/formatDimensionValue";
import { formatMetricValue } from "../../utils/formatMetricValue";
import styles from "./LineChartView.module.scss";

export const LineChartView = ({
  data,
}: {
  data: GoogleAnalyticsPropertyReport | GoogleAnalyticsPropertyRealtimeReport;
}) => {
  const chartTheme = useChartTheme();

  if (!data.dimension_headers || data.dimension_headers.length === 0) {
    throw new Error("Line chart requires at least one dimension");
  }

  // Show the year in date axis labels only when the range crosses
  // calendar years — otherwise repeating "2026" on every tick is noise.
  const dateDimensionIdx = data.dimension_headers.findIndex(
    (h) => h.name === "date",
  );
  const showYear =
    dateDimensionIdx === -1
      ? true
      : spansMultipleYears(
          (data.rows || [])
            .map((row) => {
              const value = row.dimension_values?.[dateDimensionIdx]?.value;

              return value ? parseDimensionDate(value) : null;
            })
            .filter((d): d is Date => d !== null),
        );

  const categories = useMemo(
    () =>
      (data.rows || []).map((row) =>
        row.dimension_values
          ?.map(({ value }, idx) => {
            return formatDimensionValue(
              value,
              data.dimension_headers![idx].name,
              showYear,
            );
          })
          .join(" - "),
      ),
    [data.rows, data.dimension_headers, showYear],
  );

  const series = useMemo(
    () =>
      data.metric_headers.map(({ name, type }, idx) => {
        if (!(type in PARSERS)) {
          throw new Error(
            `Line chart does not support metrics of ${type} type`,
          );
        }

        return {
          name,
          // Area series + line chart container = smooth line with
          // gradient fill below — same pattern Visibility Engine uses
          // for visits/waf.
          type: "area" as const,
          data: (data.rows || []).map((row) =>
            PARSERS[type](row.metric_values[idx].value),
          ),
        };
      }),
    [data.metric_headers, data.rows],
  );

  // Check if any metrics are float type
  const hasFloatMetrics = data.metric_headers.some(
    ({ type }) => type === "TYPE_FLOAT" || type === "TYPE_PERCENT",
  );

  const options = useMemo(
    () => ({
      chart: {
        type: "line" as const,
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: chartTheme.fontFamily,
        // Disable subsequent-update animations so Apex's internal
        // resize observer (which fires on subtle parent layout shifts
        // during page scroll) doesn't replay the animation each time.
        // Initial mount animation still plays — useChartTheme is now
        // referentially stable, so no spurious update lands within the
        // mount-animation window to short-circuit it.
        animations: {
          enabled: true,
          dynamicAnimation: { enabled: false },
        },
      },
      colors: chartTheme.palette,
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.35,
          opacityTo: 0.02,
          stops: [0, 100],
        },
      },
      stroke: { curve: "smooth" as const, width: 2 },
      dataLabels: { enabled: false },
      grid: {
        borderColor: chartTheme.gridBorder,
        strokeDashArray: 4,
      },
      xaxis: {
        categories,
        labels: {
          style: {
            colors: chartTheme.textGrey,
            fontSize: "12px",
            fontFamily: chartTheme.fontFamily,
          },
          rotate: -30,
          hideOverlappingLabels: true,
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
        tickAmount: Math.min(categories.length, 14),
      },
      yaxis: {
        labels: {
          style: {
            colors: chartTheme.textGrey,
            fontSize: "12px",
            fontFamily: chartTheme.fontFamily,
          },
          formatter: (value: number) =>
            formatMetricValue(
              value.toString(),
              hasFloatMetrics ? "TYPE_FLOAT" : "TYPE_INTEGER",
            ),
        },
      },
      tooltip: {
        theme: chartTheme.theme,
        y: {
          formatter: (value: number) =>
            formatMetricValue(
              value.toString(),
              hasFloatMetrics ? "TYPE_FLOAT" : "TYPE_INTEGER",
            ),
          title: {
            formatter: formatMetricOrDimensionName,
          },
        },
      },
      legend: { show: false },
    }),
    [chartTheme, categories, hasFloatMetrics],
  );

  return (
    <Chart
      className={styles.root}
      options={options}
      series={series}
      type="line"
      height="100%"
    />
  );
};

const PARSERS: Record<string, (value: string) => number> = {
  TYPE_INTEGER: parseInt,
  TYPE_FLOAT: parseFloat,
  TYPE_PERCENT: (value: string) => parseFloat(value) * 100,
};
